import React, { useEffect, useRef, useState } from "react";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import styles from "./campaigns.page.module.scss";

export const getMapBoundsPayload = (map) => {
  const bounds = map?.getBounds?.();
  if (!bounds) return null;
  const southWest = bounds.getSouthWest();
  const northEast = bounds.getNorthEast();
  return {
    south: southWest.lat(),
    west: southWest.lng(),
    north: northEast.lat(),
    east: northEast.lng(),
  };
};

export const isInsideMapBounds = (record, bounds) =>
  Boolean(bounds) &&
  Number.isFinite(record?.latitude) &&
  Number.isFinite(record?.longitude) &&
  record.latitude >= bounds.south &&
  record.latitude <= bounds.north &&
  (bounds.west <= bounds.east
    ? record.longitude >= bounds.west && record.longitude <= bounds.east
    : record.longitude >= bounds.west || record.longitude <= bounds.east);

const Corner = ({ name, latitude, longitude }) => {
  const coordinates = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
  const [copied, setCopied] = useState(false);
  const [fallback, setFallback] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const copySequenceRef = useRef(0);

  useEffect(() => {
    setCopied(false);
    setFallback(false);
    return () => {
      clearTimeout(timerRef.current);
      copySequenceRef.current += 1;
    };
  }, [coordinates]);

  useEffect(() => {
    if (fallback) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [fallback]);

  const copy = async () => {
    const sequence = ++copySequenceRef.current;
    try {
      await navigator.clipboard.writeText(coordinates);
      if (sequence !== copySequenceRef.current) return;
      setFallback(false);
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      if (sequence !== copySequenceRef.current) return;
      setFallback(true);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  };

  return (
    <span className={styles.mapBoundsCorner}>
      <strong>{name}</strong>
      {fallback ? (
        <input
          ref={inputRef}
          className={styles.mapBoundsInput}
          aria-label={`${name} coordinates`}
          title="Copy selected coordinates with Ctrl+C or Command+C"
          value={coordinates}
          readOnly
          onFocus={(event) => event.target.select()}
        />
      ) : (
        <span>{coordinates}</span>
      )}
      <button
        type="button"
        className={`${styles.mapBoundsCopy} ${copied ? styles.mapBoundsCopied : ""}`}
        title={`Copy ${name} coordinates`}
        aria-label={`Copy ${name} coordinates`}
        onClick={copy}
      >
        {copied ? <CheckIcon aria-hidden="true" /> : <FileCopyOutlinedIcon aria-hidden="true" />}
      </button>
    </span>
  );
};

const MapViewBounds = ({ bounds, hasGpsData }) => (
  <div className={styles.mapBounds} aria-label="View bounds">
    <strong className={styles.mapBoundsLabel}>View bounds</strong>
    {hasGpsData === false ? (
      <em>No GPS data for this campaign</em>
    ) : bounds ? (
      [
        ["NW", bounds.north, bounds.west],
        ["NE", bounds.north, bounds.east],
        ["SW", bounds.south, bounds.west],
        ["SE", bounds.south, bounds.east],
      ].map(([name, latitude, longitude]) => (
        <Corner key={name} name={name} latitude={latitude} longitude={longitude} />
      ))
    ) : (
      <span>Waiting for map bounds...</span>
    )}
  </div>
);

export default MapViewBounds;
